import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { sampleService, sampleErrorMessage } from '../../Services/SampleService';
import SpinnerModal from '../../Spinner/SpinnerModal';
import { formatLocalTime } from '../../Utils/dateUtils';
import SampleCollectionDialog from './SampleCollectionDialog';
import RejectSampleDialog from './RejectSampleDialog';
import { SampleStatusChip, sourceLabel } from './sampleUi';
import '../../UI/PatientList.css';
import './Samples.css';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const toDateStr = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const initialFilters = () => ({
  status: '',
  source: '',
  sampleTypeId: '',
  startDate: toDateStr(new Date()),
  endDate: toDateStr(new Date()),
  searchTerm: ''
});

// Lab-wide list of samples with filters, for finding a specimen and
// receiving/rejecting it.
const SampleTracking = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(initialFilters);
  const [types, setTypes] = useState([]);
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const fetchSamples = async (f = filters, pageNumber = page, size = pageSize) => {
    try {
      setIsLoading(true);
      const data = await sampleService.getList({ ...f, pageNumber, pageSize: size });
      setSamples(data?.items || []);
      setTotalCount(data?.totalCount || 0);
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to load samples'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    sampleService.getTypes(true).then(setTypes).catch(() => setTypes([]));
    fetchSamples(filters, 1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (f = filters) => {
    setPage(1);
    setResetPaginationToggle((prev) => !prev);
    fetchSamples(f, 1, pageSize);
  };

  const clearFilters = () => {
    const f = { ...initialFilters(), startDate: '', endDate: '' };
    setFilters(f);
    applyFilters(f);
  };

  const setStatus = async (sample, status, reason) => {
    try {
      await sampleService.setStatus(sample.id, status, reason);
      toast.success(`Sample ${sample.sampleNo} marked ${status.toLowerCase()}`);
      fetchSamples();
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to update sample status'));
    }
  };

  const columns = [
    {
      name: 'Sample No.',
      selector: (row) => row.sampleNo,
      width: '130px',
      cell: (row) => <strong>{row.sampleNo}</strong>
    },
    {
      name: 'Date',
      selector: (row) => row.createdAt,
      width: '150px',
      cell: (row) => <span>{formatLocalTime(row.createdAt)}</span>
    },
    {
      name: 'Patient',
      selector: (row) => row.patientName,
      cell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span
            onClick={() => navigate(`/Home/PatientProfile/${row.patientId}`)}
            style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: 'bold' }}
          >
            {row.patientName?.toUpperCase()}
          </span>
          <span style={{ fontSize: '11px', color: 'gray' }}>Order #{row.orderId}</span>
        </div>
      )
    },
    {
      name: 'Type',
      selector: (row) => row.sampleTypeName,
      cell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{row.sampleTypeName}</span>
          {row.containerType && <span style={{ fontSize: '11px', color: 'gray' }}>{row.containerType}</span>}
        </div>
      )
    },
    {
      name: 'Source',
      selector: (row) => row.source,
      cell: (row) => <span className="sample-source">{sourceLabel(row)}</span>
    },
    {
      name: 'Tests',
      selector: (row) => row.testNames.join(', '),
      wrap: true,
      cell: (row) => <span style={{ fontSize: '11px' }}>{row.testNames.join(', ')}</span>
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      width: '110px',
      cell: (row) => (
        <div title={row.status === 'Rejected' ? row.rejectionReason : row.condition}>
          <SampleStatusChip status={row.status} />
          {row.condition && row.condition !== 'Good' && (
            <div style={{ fontSize: '10px', color: '#c62828' }}>{row.condition}</div>
          )}
        </div>
      )
    },
    {
      name: 'Actions',
      width: '230px',
      cell: (row) => (
        <div className="d-flex gap-1">
          {row.status === 'Collected' && (
            <Button size="small" variant="outlined" color="success" onClick={() => setStatus(row, 'Received')}>Receive</Button>
          )}
          {row.status !== 'Rejected' && (
            <Button size="small" variant="outlined" color="error" onClick={() => setRejectTarget(row)}>Reject</Button>
          )}
          <Button size="small" variant="outlined" onClick={() => setOrderId(row.orderId)}>Order</Button>
        </div>
      )
    }
  ];

  return (
    <>
      {isLoading && <SpinnerModal isLoading={true} />}
      <div className="container-fluid Container2">
        <div className="Common-min-height pl-child-div common-box-shadow common-bg-white common-br-3px">
          <div className="sample-filters">
            <div>
              <label className="common-Labels">Search</label>
              <input
                className="form-control common-Input"
                name="searchTerm"
                placeholder="Patient name or sample no."
                value={filters.searchTerm}
                onChange={onFilterChange}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <div>
              <label className="common-Labels">Status</label>
              <select className="form-select common-Input" name="status" value={filters.status} onChange={onFilterChange}>
                <option value="">All</option>
                <option value="Collected">Collected (awaiting receipt)</option>
                <option value="Received">Received</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="common-Labels">Source</label>
              <select className="form-select common-Input" name="source" value={filters.source} onChange={onFilterChange}>
                <option value="">All</option>
                <option value="InHouse">Collected in lab</option>
                <option value="External">Brought from outside</option>
              </select>
            </div>
            <div>
              <label className="common-Labels">Sample type</label>
              <select className="form-select common-Input" name="sampleTypeId" value={filters.sampleTypeId} onChange={onFilterChange}>
                <option value="">All</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="common-Labels">Date From</label>
              <input className="form-control common-Input" type="date" name="startDate" value={filters.startDate} onChange={onFilterChange} />
            </div>
            <div>
              <label className="common-Labels">Date To</label>
              <input className="form-control common-Input" type="date" name="endDate" value={filters.endDate} onChange={onFilterChange} />
            </div>
            <div style={{ flex: '0 0 auto', minWidth: 0 }} className="d-flex gap-2">
              <Button variant="contained" className="common-button" onClick={() => applyFilters()}>Apply</Button>
              <Button variant="outlined" onClick={clearFilters}>All dates</Button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={samples}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={1}
            paginationResetDefaultPage={resetPaginationToggle}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onChangePage={(newPage) => {
              setPage(newPage);
              fetchSamples(filters, newPage, pageSize);
            }}
            onChangeRowsPerPage={(newSize, newPage) => {
              setPageSize(newSize);
              setPage(newPage);
              fetchSamples(filters, newPage, newSize);
            }}
            fixedHeader
            fixedHeaderScrollHeight="75vh"
            highlightOnHover
            responsive
            striped
            noDataComponent={<div className="py-4 text-muted">No samples found for these filters.</div>}
          />
        </div>
      </div>

      <SampleCollectionDialog orderId={orderId} onClose={() => setOrderId(null)} onChanged={() => fetchSamples()} />
      <RejectSampleDialog
        sample={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={async (reason) => {
          await setStatus(rejectTarget, 'Rejected', reason);
          setRejectTarget(null);
        }}
      />
    </>
  );
};

export default SampleTracking;
